import axios from 'axios';
import { Auth } from './amplifyCompat';

import axiosRetry from 'axios-retry';
import buildRequestError from './buildRequestError';
import { ApiError } from '../errors';
import logResponse from './logResponse';
import logRequest from './logRequest';

export const TOKEN_TYPE = 'Bearer ';
export const REQUEST_HEADER_AUTH_KEY = 'Authorization';

const APP = import.meta.env.NX_APP;

export const METHOD = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

export async function getTokenHeader() {
  try {
    const { accessToken: { jwtToken, }, } = await Auth.currentSession();
    return { Authorization: `Bearer ${jwtToken}`, };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error getting token', error);
    return {};
  }
}

export async function getHeaders({
  useOnlyBaseHeaders = false,
  omitBaseHeaders = false,
  omitTokenHeader = false,
} = {}) {
  const headers = {
    // eslint-disable-next-line quote-props
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };

  if (useOnlyBaseHeaders) {
    return headers;
  }

  const tokenHeader = await getTokenHeader();
  const localStorageHeaders = (import.meta.env.NX_REQUEST_LOCALSTORAGE_HEADERS || '')
    .split(',')
    .filter(Boolean)
    .reduce((index, headerConfig) => {
      const [headerName, localStorageKey] = headerConfig.split(':');
      return {
        ...index,
        [headerName]: localStorage.getItem(localStorageKey) || '0',
      };
    }, {});
  return {
    'x-application': APP,
    ...!omitBaseHeaders ? headers : {},
    ...!omitTokenHeader ? tokenHeader : {},
    ...localStorageHeaders,
  };
}

export class Request {
  #controller;

  constructor() {
    this.#controller = new AbortController();
  }

  prepare() {
    this.#controller = new AbortController();
  }

  async exec(
    {
      method = METHOD.GET,
      path,
      formData = null,
      params = {},
      content = {},
      headers = {},
      config = {
        alias: null,
        useOnlyBaseHeaders: false,
        useCustomDomain: false,
        omitTokenHeader: false,
        omitResponseCode: false,
        onUploadProgress: () => { return false; },
        onDownloadProgress: () => { return false; },
      },
    }
  ) {
    if (!path) {
      throw new Error('path is required');
    }
    const stage = import.meta.env.NX_ENVIRONMENT;
    const domain = !config.useCustomDomain ? `${import.meta.env.NX_API}/` : '';
    const urlPath = path.startsWith('/') ? `${stage}${path}` : `${stage}/${path}`;
    const url = path.startsWith('http') ? new URL(path) : new URL(urlPath, domain);

    const allHeaders = {
      ...await getHeaders(config),
      ...headers,
    };

    function getBody() {
      if (method === METHOD.GET) {
        return null;
      }
      if (formData) {
        return formData;
      }
      return JSON.stringify(content);
    }

    if (params) {
      Object.entries(params).reduce((newParams, [key, value]) => {
        newParams.append(key, value);
        return newParams;
      }, url.searchParams);
    }

    const body = getBody();
    let response = null;
    try {
      logRequest(url, method, allHeaders, body, config.alias);
      axiosRetry(axios, { retries: 3, });
      response = await axios({
        url,
        method,
        headers: allHeaders,
        data: body,
        onUploadProgress: config.onUploadProgress,
        onDownloadProgress: config.onDownloadProgress,
        signal: this.#controller.signal,
      });
      const data = response.data;
      logResponse(response, path, method, config.alias);
      const isStatusOk = response.status >= 200 && response.status < 300;
      const code = data?.code || 10;
      if (!isStatusOk || (!config.omitResponseCode && code !== 10)) {
        throw buildRequestError(response, data);
      }
      return data;
    } catch (error) {
      console.error('ERROR::API : ', error);
      if (error.code === 'ERR_CANCELED') {
        throw error;
      }
      if (error instanceof ApiError) {
        throw error;
      }
      throw buildRequestError(error.response, {});
    }
  }

  abort() {
    this.#controller.abort();
  }
}

export default Request;

const stage = import.meta.env.NX_ENVIRONMENT;
const domain = `${import.meta.env.NX_API}/`;
const urlPath = `${domain}${stage}`;

export const URL_API = urlPath;

const BaseService = axios.create({
  timeout: 30000,
  baseURL: urlPath,
  headers: { [REQUEST_HEADER_AUTH_KEY]: '', },
});

export const axiosBaseQuery = () => async (request) => {
  try {
    const { data, ...meta } = await BaseService(request);
    return { data, meta, };
  } catch (axiosError) {
    const err = axiosError;
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
      },
    };
  }
};

BaseService.interceptors.request.use(
  async (config) => {
    // eslint-disable-next-line no-param-reassign
    config.headers = {
      ...config.headers,
      ...(await getHeaders()),
    };
    console.log(`REQUEST :: ${config.url}`, config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

BaseService.interceptors.response.use(
  (response) => {
    console.log(`RESPONSE :: ${response.config.url}`, response);
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const apiServices = BaseService;
